import React, { useEffect, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

export default function RichTextEditor({ value, onChange, modules, className = "", imagePicker }) {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const selectedImageRef = useRef(null);
  const draggedImageRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageWidth, setSelectedImageWidth] = useState(240);

  const removeNeighborToolbar = () => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const previous = container.previousElementSibling;
    if (previous && previous.classList.contains("ql-toolbar")) {
      previous.remove();
    }
  };

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleImageClick = (event) => {
      const image = event.target.closest("img");
      if (!image || !container.contains(image)) {
        setSelectedImage(null);
        selectedImageRef.current = null;
        return;
      }
      event.preventDefault();
      image.draggable = true;
      if (selectedImageRef.current && selectedImageRef.current !== image) {
        selectedImageRef.current.style.outline = "";
        selectedImageRef.current.style.outlineOffset = "";
      }
      image.style.outline = "2px solid #d4af37";
      image.style.outlineOffset = "3px";
      selectedImageRef.current = image;
      setSelectedImage(image);
      setSelectedImageWidth(parseInt(image.style.width, 10) || image.width || 240);
      const quill = quillRef.current;
      const blot = quill && Quill.find(image);
      if (quill && blot) {
        quill.setSelection(quill.getIndex(blot), 1, "silent");
      }
    };

    container.addEventListener("click", handleImageClick);

    const handleDragStart = (event) => {
      const image = event.target.closest("img");
      const quill = quillRef.current;
      const blot = image && Quill.find(image);
      if (!image || !container.contains(image) || !blot || !quill) return;
      draggedImageRef.current = {
        sourceIndex: quill.getIndex(blot),
        sourceUrl: image.getAttribute("src"),
        attributes: Array.from(image.attributes).reduce((result, attribute) => {
          if (attribute.name !== "src") result[attribute.name] = attribute.value;
          return result;
        }, {}),
      };
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", "email-image");
    };

    const handleDragOver = (event) => {
      if (draggedImageRef.current) event.preventDefault();
    };

    const handleDrop = (event) => {
      const draggedImage = draggedImageRef.current;
      const quill = quillRef.current;
      if (!draggedImage || !quill) return;
      event.preventDefault();
      const caretRange = document.caretRangeFromPoint?.(event.clientX, event.clientY);
      const targetBlot = caretRange ? Quill.find(caretRange.startContainer.parentElement) : null;
      let targetIndex = targetBlot ? quill.getIndex(targetBlot) : quill.getLength() - 1;
      if (targetIndex > draggedImage.sourceIndex) targetIndex -= 1;
      targetIndex = Math.max(0, Math.min(targetIndex, quill.getLength() - 1));
      quill.deleteText(draggedImage.sourceIndex, 1, "user");
      quill.insertEmbed(targetIndex, "image", draggedImage.sourceUrl, "user");
      const [insertedBlot] = quill.getLeaf(targetIndex);
      const insertedImage = insertedBlot?.domNode;
      if (insertedImage) {
        Object.entries(draggedImage.attributes).forEach(([name, attributeValue]) => {
          insertedImage.setAttribute(name, attributeValue);
        });
      }
      onChangeRef.current?.(quill.root.innerHTML);
      quill.setSelection(targetIndex + 1, 0, "silent");
      draggedImageRef.current = null;
    };

    container.addEventListener("dragstart", handleDragStart);
    container.addEventListener("dragover", handleDragOver);
    container.addEventListener("drop", handleDrop);
    return () => {
      container.removeEventListener("click", handleImageClick);
      container.removeEventListener("dragstart", handleDragStart);
      container.removeEventListener("dragover", handleDragOver);
      container.removeEventListener("drop", handleDrop);
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current || quillRef.current) {
      return undefined;
    }

    // StrictMode can replay effects in development; reset the mount node
    // before creating Quill so the toolbar is not duplicated.
    containerRef.current.innerHTML = "";
    removeNeighborToolbar();

    const quill = new Quill(containerRef.current, {
      theme: "snow",
      modules,
    });

    quillRef.current = quill;

    const toolbar = quill.getModule("toolbar");
    if (toolbar && imagePicker) {
      toolbar.addHandler("image", () => imagePicker((url) => {
        const range = quill.getSelection(true);
        quill.insertEmbed(range?.index || 0, "image", url, "user");
        quill.setSelection((range?.index || 0) + 1, "silent");
      }));
    }

    if (value) {
      quill.clipboard.dangerouslyPasteHTML(value);
    }

    const handleChange = () => {
      const nextValue = quill.root.innerHTML;
      if (onChangeRef.current) {
        onChangeRef.current(nextValue);
      }
    };

    quill.on("text-change", handleChange);

    return () => {
      quill.off("text-change", handleChange);
      const toolbarModule = quill.getModule("toolbar");
      if (toolbarModule?.container && toolbarModule.container.remove) {
        toolbarModule.container.remove();
      }
      quillRef.current = null;
      if (selectedImageRef.current) {
        selectedImageRef.current.style.outline = "";
        selectedImageRef.current.style.outlineOffset = "";
      }
      selectedImageRef.current = null;
      setSelectedImage(null);
      setSelectedImageWidth(240);
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
      removeNeighborToolbar();
    };
  }, [imagePicker, modules]);

  const resizeSelectedImage = (event) => {
    const image = selectedImageRef.current;
    const width = Number(event.target.value);
    if (!image || !width) return;
    image.style.width = `${width}px`;
    image.style.maxWidth = "100%";
    onChangeRef.current?.(quillRef.current?.root.innerHTML || "");
    setSelectedImageWidth(width);
  };

  const alignSelectedImage = (alignment) => {
    const image = selectedImageRef.current;
    const quill = quillRef.current;
    if (!image || !quill) return;
    const blot = Quill.find(image);
    if (!blot) return;
    const index = quill.getIndex(blot);
    quill.formatLine(index, 1, "align", alignment, "user");
    quill.setSelection(index, 1, "silent");
  };

  const placeSelectedImageSideBySide = () => {
    const image = selectedImageRef.current;
    if (!image) return;
    const imageParagraph = image.closest("p");
    if (!imageParagraph) return;
    const siblingParagraphs = Array.from(imageParagraph.parentElement.children).filter((element) => element !== imageParagraph && element.querySelector("img"));
    const neighbor = siblingParagraphs.find((element) => Math.abs(element.offsetTop - imageParagraph.offsetTop) < 160) || siblingParagraphs[0];
    if (!neighbor) return;
    [imageParagraph, neighbor].forEach((paragraph) => {
      paragraph.style.display = "inline-block";
      paragraph.style.verticalAlign = "top";
      paragraph.style.width = "48%";
      paragraph.style.margin = "0 1% 12px 0";
    });
    onChangeRef.current?.(quillRef.current?.root.innerHTML || "");
  };

  const deleteSelectedImage = () => {
    const image = selectedImageRef.current;
    const quill = quillRef.current;
    if (!image || !quill) return;
    image.style.outline = "";
    image.style.outlineOffset = "";
    const blot = Quill.find(image);
    if (blot) {
      quill.deleteText(quill.getIndex(blot), 1, "user");
    } else {
      image.remove();
      onChangeRef.current?.(quill.root.innerHTML);
    }
    selectedImageRef.current = null;
    setSelectedImage(null);
    setSelectedImageWidth(240);
  };

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) {
      return;
    }

    const normalizedIncoming = value || "<p><br></p>";
    const normalizedCurrent = quill.root.innerHTML || "<p><br></p>";

    if (normalizedIncoming !== normalizedCurrent) {
      const imageStyles = Array.from(quill.root.querySelectorAll("img")).reduce((styles, image) => {
        const width = image.style.width || image.getAttribute("width");
        if (width) styles[image.getAttribute("src")] = width;
        return styles;
      }, {});
      const imageLayouts = Array.from(quill.root.querySelectorAll("p")).reduce((layouts, paragraph) => {
        const image = paragraph.querySelector("img");
        if (image && paragraph.style.display === "inline-block") {
          layouts[image.getAttribute("src")] = {
            display: paragraph.style.display,
            verticalAlign: paragraph.style.verticalAlign,
            width: paragraph.style.width,
            margin: paragraph.style.margin,
          };
        }
        return layouts;
      }, {});
      const selection = quill.getSelection();
      quill.clipboard.dangerouslyPasteHTML(normalizedIncoming);
      quill.root.querySelectorAll("img").forEach((image) => {
        const width = imageStyles[image.getAttribute("src")];
        if (width) {
          image.style.width = width;
          image.style.maxWidth = "100%";
        }
        const layout = imageLayouts[image.getAttribute("src")];
        if (layout && image.parentElement) {
          Object.entries(layout).forEach(([property, propertyValue]) => {
            image.parentElement.style[property] = propertyValue;
          });
        }
      });
      onChangeRef.current?.(quill.root.innerHTML);
      if (selection) {
        quill.setSelection(selection);
      }
    }
  }, [value]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {selectedImage && (
        <div onClick={(event) => event.stopPropagation()} className="absolute right-3 top-3 z-10 flex items-center gap-2 rounded border-2 border-gold-500 bg-gray-900/95 px-3 py-2 text-xs text-white shadow-xl">
          <span>Drag image to move</span>
          <label htmlFor="selected-image-width">Width</label>
          <input
            id="selected-image-width"
            type="range"
            min="40"
            max="800"
            step="10"
            value={Math.min(800, Math.max(40, selectedImageWidth || 240))}
            onChange={resizeSelectedImage}
            className="w-32 accent-gold-500"
          />
          <span className="w-12 text-right">{selectedImageWidth || 240}px</span>
          <button type="button" onClick={() => alignSelectedImage("left")} className="rounded border border-gray-600 px-2 py-1 hover:border-gold-500">Left</button>
          <button type="button" onClick={() => alignSelectedImage("center")} className="rounded border border-gray-600 px-2 py-1 hover:border-gold-500">Center</button>
          <button type="button" onClick={() => alignSelectedImage("right")} className="rounded border border-gray-600 px-2 py-1 hover:border-gold-500">Right</button>
          <button type="button" onClick={placeSelectedImageSideBySide} className="rounded border border-gray-600 px-2 py-1 hover:border-gold-500">Side by side</button>
          <button type="button" onClick={deleteSelectedImage} className="rounded border border-red-500 px-2 py-1 text-red-300 hover:bg-red-900/40">Delete</button>
        </div>
      )}
    </div>
  );
}